import { useState, useRef, useEffect } from "react";
import { campaigns as api, type Campaign, type OptimizationResult } from "@/lib/api";
import { Loader2, X, TrendingUp, Check, ArrowRight, RefreshCw, Zap } from "lucide-react";

interface OptimizationPanelProps {
  campaign: Campaign;
  onClose: () => void;
  onFollowupCreated: () => void; // refresh the campaigns list
}

export default function OptimizationPanel({ campaign, onClose, onFollowupCreated }: OptimizationPanelProps) {
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState<OptimizationResult | null>(null);
  const [error, setError]         = useState<string | null>(null);
  const [applying, setApplying]   = useState<string | null>(null); // subject being applied
  const [applied, setApplied]     = useState<string | null>(null); // subject successfully applied
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkDone, setBulkDone]   = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const inFlight = useRef(false);

  const isPersonalized = (campaign.subject || "").includes("{{meta.personalized_subject}}");

  const runOptimize = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await api.optimize(campaign.id);
      setResult(res);
    } catch (e: any) {
      setError(e.message || "Failed to generate suggestions — please try again");
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  };

  const handleApply = async (subject: string) => {
    if (!result) return;
    setApplying(subject);
    setError(null);
    try {
      await api.followup(campaign.id, subject, result.optimizationId);
      setApplied(subject);
      onFollowupCreated();
    } catch (e: any) {
      setError(e.message || "Failed to create follow-up campaign");
    } finally {
      setApplying(null);
    }
  };

  const handleBulkOptimize = async () => {
    if (bulkLoading) return;
    setBulkLoading(true);
    setBulkMessage(null);
    setError(null);
    try {
      const res = await api.optimizeSubjects(campaign.id);
      setBulkDone(true);
      setBulkMessage(res.message);
    } catch (e: any) {
      setError(e.message || "Failed to start bulk optimization");
    } finally {
      setBulkLoading(false);
    }
  };

  // Auto-run on mount (skip for personalized — the standard suggestions aren't useful)
  useEffect(() => { if (!isPersonalized) runOptimize(); }, []);

  const sent    = result?.sent    ?? Number(campaign.sent_count  ?? 0);
  const openRate = result
    ? result.openRate
    : sent > 0 ? ((Number(campaign.open_count ?? 0) / sent) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="w-full max-w-lg bg-paper flex flex-col h-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06] bg-white">
          <div>
            <h2 className="font-bold text-ink flex items-center gap-2">
              <TrendingUp size={16} className="text-coral" /> Optimize Campaign
            </h2>
            <p className="text-xs text-slate mt-0.5 truncate max-w-[300px]">{campaign.name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/[0.04] text-slate transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-5">

          {/* Current performance */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Open Rate", value: `${openRate.toFixed(1)}%`, highlight: openRate < 20 },
              { label: "Sent",      value: (result?.sent ?? Number(campaign.sent_count ?? 0)).toLocaleString(), highlight: false },
              { label: "Opens",     value: (result?.opens ?? Number(campaign.open_count ?? 0)).toLocaleString(), highlight: false },
            ].map(({ label, value, highlight }) => (
              <div key={label} className={`rounded-xl border p-3 text-center ${highlight ? "border-red-200 bg-red-50" : "border-black/[0.06] bg-white"}`}>
                <p className={`text-xl font-bold ${highlight ? "text-red-600" : "text-ink"}`}>{value}</p>
                <p className="text-[11px] text-slate mt-0.5">{label}</p>
                {highlight && <p className="text-[10px] text-red-500 mt-0.5">Below 20% avg</p>}
              </div>
            ))}
          </div>

          {/* Original subject */}
          <div className="rounded-xl border border-black/[0.06] bg-white p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-1">Original Subject</p>
            <p className="text-sm font-semibold text-ink">{campaign.subject || "(no subject)"}</p>
          </div>

          {/* ── Personalised campaign: bulk optimize ── */}
          {isPersonalized && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Zap size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Personalised Subjects Detected</p>
                  <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                    This campaign uses unique subject lines per subscriber. The AI will rewrite each
                    non-opener's subject individually for better open rates. Originals are backed up
                    to <code className="bg-amber-100 px-1 rounded text-[11px]">metadata.original_subject</code>.
                  </p>
                </div>
              </div>

              {bulkDone ? (
                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-xs text-green-700 font-medium flex items-center gap-2">
                  <Check size={14} /> {bulkMessage}
                </div>
              ) : (
                <button
                  onClick={handleBulkOptimize}
                  disabled={bulkLoading}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50 transition-colors"
                >
                  {bulkLoading
                    ? <><Loader2 size={14} className="animate-spin" /> Optimizing subjects…</>
                    : <><Zap size={14} /> Optimize All Non-Opener Subjects</>
                  }
                </button>
              )}

              {bulkDone && (
                <p className="text-xs text-amber-700">
                  Use "Resend to new" to send the campaign again — non-openers will get their improved subject lines automatically.
                </p>
              )}
            </div>
          )}

          {/* ── Standard (non-personalised) flow ── */}
          {!isPersonalized && (
            <>
              {/* Loading state */}
              {loading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate">
                  <Loader2 size={28} className="animate-spin" />
                  <p className="text-sm font-medium">Analysing campaign performance…</p>
                </div>
              )}

              {/* Error */}
              {error && !loading && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-3">
                  <div className="flex-1">{error}</div>
                  <button onClick={runOptimize} className="text-red-700 underline text-xs shrink-0">Retry</button>
                </div>
              )}

              {/* Suggestions */}
              {result && !loading && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate">
                      AI Suggestions
                    </p>
                    <button
                      onClick={runOptimize}
                      className="inline-flex items-center gap-1 text-xs text-slate hover:text-coral transition-colors"
                    >
                      <RefreshCw size={11} /> Regenerate
                    </button>
                  </div>

                  {result.suggestions.length === 0 ? (
                    <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
                      No specific improvements identified — your campaign performed well!
                    </div>
                  ) : (
                    result.suggestions.map((s, i) => (
                      <div key={i} className={`rounded-xl border p-4 space-y-2 transition-colors ${
                        applied === s.subject ? "border-green-300 bg-green-50" : "border-black/[0.06] bg-white"
                      }`}>
                        <p className="text-sm font-semibold text-ink">{s.subject}</p>
                        <p className="text-xs text-slate leading-relaxed">{s.rationale}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-teal">{s.predictedImprovement}</span>
                          {applied === s.subject ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
                              <Check size={12} /> Follow-up created
                            </span>
                          ) : (
                            <button
                              onClick={() => handleApply(s.subject)}
                              disabled={!!applying || !!applied}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-coral text-white text-xs font-semibold hover:bg-coral-dark disabled:opacity-50 transition-colors"
                            >
                              {applying === s.subject
                                ? <><Loader2 size={11} className="animate-spin" /> Creating…</>
                                : <>Apply <ArrowRight size={11} /></>
                              }
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  {applied && (
                    <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-xs text-green-700 font-medium">
                      ✓ A new draft campaign has been created with the improved subject line and the same email content. Find it in your Campaigns list.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Error for personalized flow */}
          {isPersonalized && error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-3">
              <div className="flex-1">{error}</div>
              <button onClick={handleBulkOptimize} className="text-red-700 underline text-xs shrink-0">Retry</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
